import React from 'react';
import { ClipboardList, FileText, LayoutGrid } from 'lucide-react';

import type { Profile, QuizSummary } from '../lib/api';
import { MyQuizzes } from './MyQuizzes';
import { ExamsView } from './ExamsView';
import { TasksView } from './TasksView';

export type MineTab = 'quizzes' | 'exams' | 'tasks';

/**
 * «Mis trivias, exámenes y tareas»: todo lo que ha creado la persona, en
 * tres pestañas. Se llega desde el menú de la cuenta (cada pestaña tiene su
 * entrada) y desde el botón del perfil.
 */
export const MyContentView: React.FC<{
  profile: Profile;
  tab: MineTab;
  onTab: (tab: MineTab) => void;
  onHostQuiz: (quiz: QuizSummary) => void;
  onEditQuiz: (quizId: string) => void;
  onCreateQuiz: () => void;
  onCatalogChange: () => void;
  examCreateRequest: number;
  taskCreateRequest: number;
}> = ({
  profile,
  tab,
  onTab,
  onHostQuiz,
  onEditQuiz,
  onCreateQuiz,
  onCatalogChange,
  examCreateRequest,
  taskCreateRequest,
}) => (
  <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
    <h1 className="text-3xl font-display font-bold">Mis trivias, exámenes y tareas</h1>

    <div role="tablist" aria-label="Lo que has creado" className="flex border-b border-ink-700">
      <TabButton active={tab === 'quizzes'} onClick={() => onTab('quizzes')} icon={<LayoutGrid className="w-4 h-4" />}>
        Mis trivias
      </TabButton>
      <TabButton active={tab === 'exams'} onClick={() => onTab('exams')} icon={<FileText className="w-4 h-4" />}>
        Mis exámenes
      </TabButton>
      <TabButton active={tab === 'tasks'} onClick={() => onTab('tasks')} icon={<ClipboardList className="w-4 h-4" />}>
        Mis tareas
      </TabButton>
    </div>

    <div role="tabpanel">
      {tab === 'quizzes' ? (
        <MyQuizzes onHost={onHostQuiz} onEdit={onEditQuiz} onCreate={onCreateQuiz} onCatalogChange={onCatalogChange} />
      ) : tab === 'exams' ? (
        <ExamsView profile={profile} createRequest={examCreateRequest} />
      ) : (
        <TasksView profile={profile} createRequest={taskCreateRequest} />
      )}
    </div>
  </div>
);

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ active, onClick, icon, children }) => (
  <button
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`flex items-center gap-2 px-2.5 sm:px-4 py-3 -mb-px whitespace-nowrap border-b-2 text-sm font-semibold transition-colors ${
      active ? 'border-ember-500 text-ink-50' : 'border-transparent text-ink-400 hover:text-ink-100'
    }`}
  >
    {/* En el teléfono no caben las tres pestañas con icono. */}
    <span className="hidden sm:inline-flex">{icon}</span>
    {children}
  </button>
);
